import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, FilePen, Trash2, Edit, Save } from 'lucide-react';
import { toast } from 'sonner';

// Las palabras clave: con qué texto de la cita (procedimiento o nota de
// quirófano) se preselecciona esta plantilla. Son de cada clínica —sus
// sinónimos y las abreviaturas de su equipo— y se escriben una por línea.
// «palabra*» casa por prefijo, «a & b» pide las dos, «!palabra» la veta.
const aLineas = (lista) => (lista || []).join('\n');
const deLineas = (texto) => String(texto || '').split('\n').map((l) => l.trim()).filter(Boolean);

function TemplateDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  // Each section carries a stable local _uid used only for React keys; stripped before POST.
  const newSection = (title = '', body = '') => ({ _uid: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, title, body });
  const withUids = (sections) => (sections || []).map(s => s._uid ? s : { ...newSection(s.title, s.body) });
  const [form, setForm] = useState(() => {
    const base = initial || { code: '', name: '', surgery_label: '', intro: '', sections: [{ title: '', body: '' }], consent_text: '', authorizations: [], active: true };
    return { ...base, sections: withUids(base.sections) };
  });
  const [authStr, setAuthStr] = useState((initial?.authorizations || []).join('\n'));
  const [keywordsStr, setKeywordsStr] = useState(aLineas(initial?.keywords));
  useEffect(() => {
    if (open) {
      const i = initial || { code: '', name: '', surgery_label: '', intro: '', sections: [{ title: '', body: '' }], consent_text: '', authorizations: [], active: true };
      setForm({ ...i, sections: withUids(i.sections) });
      setAuthStr((i.authorizations || []).join('\n'));
      setKeywordsStr(aLineas(i.keywords));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSection = (i, k, v) => setForm(f => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addSection = () => setForm(f => ({ ...f, sections: [...f.sections, newSection()] }));
  const removeSection = (i) => setForm(f => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  const submit = async (e) => {
    e.preventDefault();
    // Strip internal _uid from sections before persisting.
    const cleanSections = (form.sections || []).map(({ _uid, ...rest }) => rest);
    const payload = {
      ...form, sections: cleanSections, authorizations: authStr.split('\n').filter(Boolean),
      keywords: deLineas(keywordsStr),
    };
    try {
      if (isEdit) { await api.put(`/api/consent-templates/${initial.id}`, payload); toast.success('Actualizada'); }
      else { await api.post('/api/consent-templates', payload); toast.success('Creada'); }
      onSaved && onSaved(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Código</Label><Input required value={form.code} disabled={isEdit} onChange={(e) => setF('code', e.target.value)} /></div>
            <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setF('name', e.target.value)} /></div>
            <div><Label>Etiqueta (badge)</Label><Input required value={form.surgery_label} onChange={(e) => setF('surgery_label', e.target.value)} /></div>
          </div>
          <div><Label>Introducción (DECLARO)</Label><Textarea rows={3} value={form.intro} onChange={(e) => setF('intro', e.target.value)} /></div>
          <div>
            <div className="flex items-center justify-between mb-1"><Label>Secciones</Label><Button type="button" variant="outline" size="sm" onClick={addSection}><Plus className="w-3.5 h-3.5 mr-1" /> Añadir sección</Button></div>
            {form.sections.map((s, i) => (
              <div key={s._uid} className="border border-border rounded-lg p-3 mb-2 space-y-2">
                <div className="flex items-center gap-2"><Input placeholder="Título" value={s.title} onChange={(e) => setSection(i, 'title', e.target.value)} /><Button type="button" variant="ghost" size="icon" onClick={() => removeSection(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></div>
                <Textarea rows={3} placeholder="Cuerpo de la sección…" value={s.body} onChange={(e) => setSection(i, 'body', e.target.value)} />
              </div>
            ))}
          </div>
          <div><Label>Texto del consentimiento (sigue a "CONSIENTO")</Label><Textarea rows={2} value={form.consent_text} onChange={(e) => setF('consent_text', e.target.value)} /></div>
          <div><Label>Autorizaciones (una por línea)</Label><Textarea rows={2} value={authStr} onChange={(e) => setAuthStr(e.target.value)} /></div>
          <div>
            <Label>Palabras clave para preseleccionarla (una por línea)</Label>
            <Textarea rows={3} value={keywordsStr} onChange={(e) => setKeywordsStr(e.target.value)}
              placeholder={'Nombre del procedimiento, sinónimos y abreviaturas del equipo'}
              data-testid="template-keywords" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Si el procedimiento o la nota de quirófano de una cita dicen alguna, la plantilla se prepara sola.
              Palabras enteras, sin importar tildes ni mayúsculas; «palabra*» vale para lo que empiece así,
              «a &amp; b» pide las dos y «!palabra» impide preseleccionarla. Sin palabras clave, se elige a mano.
            </p>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"><Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsentTemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fetchItems = async () => { setLoading(true); try { const r = await api.get('/api/consent-templates'); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { fetchItems(); }, []);
  const onDelete = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/api/consent-templates/${id}`); toast.success('Eliminada'); fetchItems(); };
  return (
    <div>
      <PageHeader title="Plantillas de consentimientos" subtitle={`${items.length} plantillas (sin sección de revocación)`}
        actions={<Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="template-new"><Plus className="w-4 h-4 mr-2" /> Nueva plantilla</Button>} />
      <div className="cp-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <EmptyState icon={FilePen} title="Sin plantillas" description="Añade la primera." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Código</th><th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th><th className="py-3 px-4 font-semibold text-[#13231E]">Badge</th><th className="py-3 px-4 font-semibold text-[#13231E]">Secciones</th><th className="py-3 px-4 font-semibold text-[#13231E]">Palabras clave</th><th></th></tr></thead>
            <tbody>{items.map(t => <tr key={t.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="template-row">
              <td className="py-3 px-4 text-xs font-mono">{t.code}</td>
              <td className="py-3 px-4 font-medium">{t.name}</td>
              <td className="py-3 px-4 text-xs">{t.surgery_label}</td>
              <td className="py-3 px-4 text-xs">{(t.sections || []).length} secciones</td>
              <td className="py-3 px-4 text-xs text-muted-foreground max-w-[220px] truncate" title={(t.keywords || []).join(', ')}>
                {(t.keywords || []).length ? (t.keywords || []).join(', ') : 'Ninguna (se elige a mano)'}
              </td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid="template-edit"><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody>
          </table></div>}
      </div>
      <TemplateDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} />
    </div>
  );
}
